<template>
  <div
    ref="observerEl"
    :style="{ minHeight: !shownProducts.length ? '150px' : undefined }"
  >
    <div
      v-if="!loading && shownProducts.length"
      class="ProductAccessories layout-container"
      :class="{
        'mobOnly:mb-48': design === 'b2c',
      }"
    >
      <a id="ProductAccessories" />
      <div
        class="mb-24"
        :class="{
          'text-center': design === 'b2c'
        }"
      >
        <div class="type-headline-xl ">
          {{ design === 'b2c' ? $t('accessories.headline.b2c') : $t('accessories.headline') }}
        </div>
        <div v-if="design === 'b2c'" class="mt-12 max-w-[800px] mx-auto text-text-subtle mb-48">{{ $t('accessories.text.b2c') }}</div>
      </div>

      <div v-if="design === 'b2c'" class="desk:mb-96 flex flex-col desk:grid grid-cols-2 gap-16">
        <div v-for="cat in shownProducts" :key="cat.code">
          <B2CAccessoryProductCard
            :product="cat.products[0]"
          />
        </div>
      </div>

      <div v-else>
        <div
          class="desk:grid gap-8"
          :class="{
            'grid-cols-5': shownProducts.length <= 5,
            'grid-cols-6': shownProducts.length >= 6,
          }"
        >
          <div v-for="(cat, index) in shownProducts" :key="cat.code">
            <div class="type-headline-md mb-16">{{ $t(`accessories.relationTypes.${cat.code}`) }}</div>
            <ProductCard
              :product="cat.products[0]"
              :enable-quick-buy="true"
              :placement="index"
              :design="uiStore.isMobile ? 'accessories':'standard'"
            />

            <button
              class="desk:hidden btn btn--secondary w-full mb-16"
              type="button"
              @click="quickbuyOpen = cat.code"
            >
              {{ $t('accessories.showMore', { num: cat.products.length }) }}
            </button>
          </div>
        </div>
        <div class="mobOnly:hidden grid grid-cols-5 gap-8">
          <div v-for="cat in shownProducts" :key="cat.code">
            <div class="type-headline-md mb-16">
              <button
                class="btn btn--secondary w-full"
                type="button"
                @click="quickbuyOpen = cat.code"
              >
                {{ $t('accessories.showMore', { num: cat.products.length }) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="hasStartedLoading && loading" class="layout-container">
      <div class="animate-pulse bg-lightest h-24 w-48 mb-16 rounded" />
      <div class="grid grid-cols-2 desk:grid-cols-4 gap-12">
        <div class="bg-lightest h-140 rounded" />
        <div class="bg-lightest h-140 rounded" />
        <div class="bg-lightest h-140 rounded hidden desk:block" />
        <div class="bg-lightest h-140 rounded hidden desk:block" />
      </div>
    </div>
  </div>
  <transition name="fromRight">
    <QuickBuyMultiple
      v-if="quickbuyOpen !== ''"
      :key="'modal' + quickbuyOpen"
      :products-in="quickbuyPartnos"
      :category-code="quickbuyOpen"
      @close="quickbuyOpen = ''"
    />
  </transition>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import ProductCard from '~/components/product/ProductCard.vue';
import QuickBuyMultiple from '~/components/product/QuickBuyMultiple.vue';
import { useUiStore } from '~/store/ui';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
import B2CAccessoryProductCard from '~/components/product-page/B2CAccessoryProductCard.vue';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';

const { getExactVariantByPartNo, lastError, lastErrorCode } = useAlgoliaFetch();

const uiStore = useUiStore();

interface Props {
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
  design?: 'b2b' | 'b2c'
}

const props = withDefaults(defineProps<Props>(), {
  design: 'b2b',
});

const includedCodes = computed(() => {

  if (props.design === 'b2c') {
    return [
      NorceRelatedProductTypeCodes.Mouldings,
      NorceRelatedProductTypeCodes.Skirtings,
      //NorceRelatedProductTypeCodes.Profiles,
      NorceRelatedProductTypeCodes.Underlays,
      //NorceRelatedProductTypeCodes.Installation,
      NorceRelatedProductTypeCodes.Aftercare,
    ] as string[];
  }

  return [
    NorceRelatedProductTypeCodes.Mouldings,
    NorceRelatedProductTypeCodes.Skirtings,
    NorceRelatedProductTypeCodes.Profiles,
    NorceRelatedProductTypeCodes.Underlays,
    NorceRelatedProductTypeCodes.Installation,
    NorceRelatedProductTypeCodes.Aftercare,
  ] as string[];
});

const observerEl = ref<HTMLElement | null>(null);
const hasStartedLoading = ref(false);
const loading = ref(false);

const shownProducts = ref<{
  code: string,
  products: ProductModel[]
}[]>([]);

const loadProducts = async() => {
  if (loading.value) {
    return;
  }
  loading.value = true;

  const allPartNos = props.activeVariant.relatedProducts
    .filter(f => includedCodes.value.includes(f.code || ''))
    .map(m => m.partNo);

  if (!allPartNos.length) {
    loading.value = false;
    return;
  }

  const res = await getExactVariantByPartNo(allPartNos);

  if (res) {
    shownProducts.value = includedCodes.value.map((code) => {
      const inCode = props.activeVariant.relatedProducts.filter(f => f.code === code);
      const products = [] as ProductModel[];
      inCode.forEach((i) => {
        const hasProduct = res.hits.find(pf => pf.partNo === i.partNo);
        if (hasProduct) {
          products.push(Product.create([ hasProduct ], true));
        }
      });
      return {
        code,
        products,
      };
    }).filter(f => f.products.length > 0);

  } else {
    console.log(lastError.value);
    console.log(lastErrorCode.value);
  }

  loading.value = false;
};

const startLoading = () => {
  if (hasStartedLoading.value) {
    return;
  }
  hasStartedLoading.value = true;
  loadProducts();
};

onMounted(() => {
  if (!observerEl.value) {
    return;
  }
  let observer: IntersectionObserver | null = null;
  let waitTimeout: ReturnType<typeof setTimeout> | null = null;
  const setupObserver = () => {
    if (!observerEl.value) {
      return;
    }
    const observerOptions = {
      root: null,
      threshold: 0.1,
      rootMargin: '-50px 0px',
    };
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
          startLoading();
          observer?.unobserve(entry.target);
        }
      });
    }, observerOptions);
    observer.observe(observerEl.value);
  };

  const WAIT_STEP_MS = 100;
  const MAX_WAIT_MS = 800;
  let waitedMs = 0;
  const scheduleObserver = () => {
    if (window.scrollY > 0 && waitedMs < MAX_WAIT_MS) {
      waitedMs += WAIT_STEP_MS;
      waitTimeout = setTimeout(scheduleObserver, WAIT_STEP_MS);
      return;
    }
    setupObserver();
  };

  scheduleObserver();
  onBeforeUnmount(() => {
    if (waitTimeout) {
      clearTimeout(waitTimeout);
    }
    if (observer && observerEl.value) {
      observer.unobserve(observerEl.value);
    }
  });
});

const quickbuyOpen = ref('');
const quickbuyPartnos = computed<ProductModel[]>(()=>{
  const open = shownProducts.value.find(f => f.code === quickbuyOpen.value);
  if (open) {
    return open.products;
  }
  return [];
});

watch(
  () => props.activeVariant,
  () => {
    if (hasStartedLoading.value) {
      loadProducts();
    }
  }
);

</script>
